import { Check, CircleAlert } from 'lucide-react'

import type { UploadItem } from '../../upload/types'
import { formatBytes } from '../../lib/format'
import { ICON_STROKE, Progress, cx } from '../../ui'

interface Props {
  items: UploadItem[]
  onCancel: (itemId: string) => void
}

const STATUS_LABEL: Record<UploadItem['status'], string> = {
  queued: 'En cola',
  preparing: 'Preparando',
  uploading: 'Subiendo',
  finalizing: 'Cerrando',
  done: 'Listo',
  failed: 'Error',
  canceled: 'Cancelado',
  resumable: 'Reanudable',
}

export function UploadQueue({ items, onCancel }: Props) {
  if (items.length === 0) return null

  return (
    <ul className="flex flex-col gap-2">
      {items.map((item) => {
        const percent = item.sizeBytes === 0 ? 0 : Math.round((item.bytesSent / item.sizeBytes) * 100)
        const active = item.status === 'uploading' || item.status === 'preparing'
        const failed = item.status === 'failed'

        return (
          <li
            key={item.id}
            className={cx(
              'rounded-card border p-3',
              failed ? 'border-danger-line bg-danger-subtle' : 'border-line',
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                {/* truncate + min-w-0: sin esto, un nombre largo rompe el
                    layout en pantallas de 360 px. */}
                <p className="truncate text-label font-medium text-ink">{item.filename}</p>
                <p className="mt-0.5 text-caption text-ink-subtle" data-numeric>
                  {formatBytes(item.sizeBytes)}
                  {item.strategy === 'multipart' && item.totalParts > 0 && (
                    <>
                      {' · '}
                      {item.completedParts}/{item.totalParts} partes
                    </>
                  )}
                  {' · '}
                  <span className={failed ? 'font-medium text-danger' : undefined}>
                    {STATUS_LABEL[item.status]}
                  </span>
                </p>
              </div>

              {active && (
                <button
                  onClick={() => onCancel(item.id)}
                  aria-label={`Cancelar ${item.filename}`}
                  className="action shrink-0 text-caption text-ink-subtle transition-colors hover:text-danger"
                >
                  Cancelar
                </button>
              )}

              {item.status === 'done' && (
                <Check
                  size={17}
                  strokeWidth={2.5}
                  aria-hidden
                  className="shrink-0 text-positive"
                />
              )}

              {failed && (
                <CircleAlert
                  size={17}
                  strokeWidth={ICON_STROKE}
                  aria-hidden
                  className="shrink-0 text-danger"
                />
              )}
            </div>

            {active && (
              <Progress
                value={percent}
                label={`Progreso de ${item.filename}`}
                className="mt-2 h-1.5"
              />
            )}

            {item.error !== null && (
              <p role="alert" className="mt-2 text-caption text-danger">
                {item.error}
              </p>
            )}
          </li>
        )
      })}
    </ul>
  )
}
